import * as React from 'react';
import { Button } from 'antd';
import { HeartFilled } from '@ant-design/icons';
import Config from './engine/Config';
import Tile from './engine/Tile';
import VisualMemoryEngine from './engine/VisualMemoryEngine';
import { VisualMemoryBox, GameBoard, TileBox, InfoBarBox, LivesBox, LevelBox, BoardBox, HeartBox, LevelAnimation, GameOverAnimation } from './VisualMemory.css';
import { GameOverButton, GameOverInfoBox, GameOverRow } from '../SequenceMemory/SequenceMemory.css';
import ShowTime from '../../components/components/ShowTime/ShowTime';
import UserContext from '../../contexts/UserContext';
import { displayTime } from '../functions';
import gamesList from '../../components/components/ChooseGame/gamesList';
import { saveUserRecord } from '../api';

const vm = new VisualMemoryEngine();

const VisualMemory = (): JSX.Element => {
    const { useEffect, useState, useContext } = React;
    const {user} = useContext(UserContext);
    const [loading, setLoading] = useState(true);
    const [levelAnimation, setLevelAnimation] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [nextLevel, setNextLevel] = useState(1);
    const [tiles, setTiles] = useState<Tile[]>([]);
    const [gameConfig, setGameConfig] = useState<Config>(new Config());
    const [gameStarted, setGameStarted] = useState(false);
    const [startTime, setStartTime] = useState(0);
    const [gameTime, setGameTime] = useState(0);
    const [scoreSaved, setScoreSaved] = useState(false);

    useEffect(() => {
        start();
    }, []);

    const start = () => {
        vm.config.level = 1;
        vm.config.lives = 3;
        vm.config.width = 3;
        vm.config.height = 3;
        vm.initGame();
        setGameConfig(vm.config);
        setTiles(vm.tiles);
    }

    const showTypedTiles = (_tiles?: Tile[]) => {
        const oldTiles = _tiles ? _tiles : tiles;
        const newTiles = [...oldTiles.map(x => {
            if (x.typed) {
                return {...x, highlight: true, rotate: true};
            }
            return x;
        })];
        setTiles(newTiles);
        setTimeout(() => {
            setTiles(oldTiles);
            setLevelAnimation(false);
            setLoading(false);
        }, 2000)
    }

    const handleClickTile = (tileNum: number) => {
        if (loading) return;
        const newTiles = [...tiles]
        newTiles[tileNum].hidden = false;
        newTiles[tileNum].rotate = true;
        setTiles(newTiles);
        // sprawdz może to już gameover
        const countNotCorrect = newTiles.filter(x => x.hidden === false && x.typed === false).length;
        if (countNotCorrect > 2) {
            if (gameConfig.lives === 1) {
                setGameOver(true);
                setGameConfig({...gameConfig, lives: 0});
                return;
            }
            setLoading(true);
            setTimeout(() => {
                newLevel(gameConfig.level, gameConfig.lives - 1);
            }, 300);
            return;
        }
        const countCorrect = newTiles.filter(x => x.hidden === false && x.typed === true).length;
        if (countCorrect >= gameConfig.memoryCount) {
            setLoading(true);
            setNextLevel(gameConfig.level + 1);
            setLevelAnimation(true);
            setTimeout(() => {
                newLevel();
            }, 300);
        }
    };

    // const sleep = (time: number): Promise<void> => new Promise(r => setTimeout(r, time));

    const newLevel = (level?: number, lives?: number) => {
        setLoading(true);
        if (level) {
            vm.config.level = level;
        } else {
            vm.config.level++;
        }
        if (lives) {
            vm.config.lives = lives;
        }
        vm.initGame();
        setGameTime(Date.now() - startTime);
        setGameConfig(vm.config);
        setTiles(vm.tiles);
        showTypedTiles(vm.tiles);
    }

    const handleStartGame = () => {
        showTypedTiles();
        setStartTime(Date.now());
        setGameStarted(true);
    };

    const renderLives = () => {
        const tlives = [];
        for (let i = 0; i < gameConfig.lives; i++) {
            tlives.push(1)
        }
        return <HeartBox>
            {tlives.length > 0 && tlives.map( x => <HeartFilled />)}
        </HeartBox>
    };

    const remakeGame = () => {
        setGameOver(false);
        start();
        newLevel(1, 3);
    };

    const saveScore = async () => {
        // vm === Visual Memory
        const gameId = gamesList.find(x => x.id === 'vm')?.gameId || 0;
        const saved = await saveUserRecord(user.id, nextLevel, gameTime, gameId);
        setScoreSaved(true);
        console.log(saved);
    };

    return (
        <VisualMemoryBox>
            {levelAnimation && <LevelAnimation>LEVEL {nextLevel}</LevelAnimation>}
            {gameOver && <GameOverAnimation>
                <GameOverInfoBox>
                    <GameOverRow>
                        LEVEL {gameConfig.level}
                    </GameOverRow>
                    <GameOverRow>
                        <ShowTime time={displayTime(gameTime)} />
                    </GameOverRow>
                    <GameOverButton>
                        <Button onClick={remakeGame}>PLAY AGAIN</Button>
                        <Button disabled={scoreSaved} style={{marginLeft: 5}} onClick={saveScore}>{scoreSaved ? 'SAVED' : 'SAVE SCORE'}</Button>
                    </GameOverButton>
                </GameOverInfoBox>
            </GameOverAnimation>}
            {gameStarted ? <BoardBox>
                <InfoBarBox>
                    <LevelBox>
                        LEVEL {gameConfig.level}
                    </LevelBox>
                    <LivesBox>
                        {renderLives()}
                    </LivesBox>
                </InfoBarBox>
                <GameBoard columns={gameConfig.width}>
                    {tiles.map((tile: Tile, tileNum: number) =>
                        <TileBox highlight={tile.highlight} typed={tile.typed} rotated={tile.rotate} hidd={tile.hidden} key={tileNum} onClick={() => handleClickTile(tileNum)}></TileBox>
                    )}
                </GameBoard>
            </BoardBox> :
            <Button onClick={handleStartGame}>START</Button>}
        </VisualMemoryBox>
    );
};

export default VisualMemory;